import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { FileDownloadModal } from '@components/file-download-modal';
import { SelectScope } from '@components/forms/offline-sales/select-scope';
import { SelectType } from '@components/forms/offline-sales/select-type';
import { IconDownload } from '@components/icons/icon-download';
import { AddBulkHolderModal } from '@components/modals/add-bulk-holder-modal';
import { AddHolderModal } from '@components/modals/add-holder-modal';
import { SummaryFormModalContent } from '@components/modals/summary-form-modal';
import { SummaryFormMultiModalContent } from '@components/modals/summary-form-multi-modal';
import { SummarySuccessModalContent } from '@components/modals/summary-success-modal';
import { STEP_INFO, STEP_SELECT_SCOPE, STEP_SELECT_TYPE, STEP_SUCCESS } from '@constants/process-steps';
import { useCheckTicketDownload } from '@hooks/bookings/use-check-ticket-download';
import { getBookingLinkUrl } from '@utils/create-booking-link';

import type { OfflineSalesStepsProps } from '../offline-sales-steps.type';
import { OFFLINE_SALES_SCOPE_PASS, OfflineSalesForm, SINGLE_OFFLINE_SELL } from '../process-offline-sales.type';

export const ProcessStepContent = ({
  step,
  offer,
  event,
  saleType,
  quantity,
  availableAddons,
  addGuestData,
  selectedAddonIds,
  email,
  isSubmitting,
  closeModal,
  handleCloseModal,
  onSubmit,
  setStep,
  scope,
  selectedPass,
  passModalChannels,
  isLoadingPassChannels,
  defaultPassChannelId,
  venueIsoCode,
  onPassHolderSubmit,
  preserveAddHolderModal,
  onPreserveAddHolderModalChange,
  closeWizardModal,
  passHolderMode,
  onSelectSinglePassHolder,
  passOptions,
  canBulkImportPass,
  isNominativePass,
  entryStep,
}: OfflineSalesStepsProps) => {
  const { t } = useTranslation();
  const methods = useFormContext<OfflineSalesForm>();
  const { handleSubmit } = methods;
  const { checkAndDownload, downloadModalProps } = useCheckTicketDownload();

  const isPassScope = scope === OFFLINE_SALES_SCOPE_PASS;

  switch (step) {
    case STEP_SELECT_SCOPE:
      return <SelectScope onContinue={() => setStep(isPassScope ? STEP_INFO : STEP_SELECT_TYPE)} />;
    case STEP_SELECT_TYPE:
      return <SelectType onContinue={() => setStep(STEP_INFO)} offer={offer} />;
    case STEP_INFO:
      if (isPassScope) {
        return (
          <>
            <AddBulkHolderModal
              isOpen={passHolderMode === 'bulk'}
              onClose={closeWizardModal}
              passId={selectedPass?.id ?? ''}
              isNominativePass={isNominativePass}
              onSelectSingle={onSelectSinglePassHolder}
              passOptions={passOptions}
              canBulkImport={canBulkImportPass}
            />
            {selectedPass && (
              <AddHolderModal
                isOpen={passHolderMode === 'single'}
                selectedPass={selectedPass}
                venueIsoCode={venueIsoCode}
                defaultChannelId={defaultPassChannelId}
                title={t('passes:addHolderModalTitle')}
                channels={passModalChannels}
                buttonPrimaryText={t('passes:addHolderModalPrimaryButton')}
                buttonSecondaryText={t('passes:addHolderModalSecondaryButton')}
                handleClose={closeWizardModal}
                handlePrimaryButtonClick={onPassHolderSubmit}
                handleSecondaryButtonClick={closeWizardModal}
                loadingChannels={isLoadingPassChannels}
                preserveModal={preserveAddHolderModal}
                handleOnCheckboxChange={onPreserveAddHolderModalChange}
              />
            )}
          </>
        );
      }
      return (
        offer &&
        event &&
        (saleType === SINGLE_OFFLINE_SELL ? (
          <SummaryFormModalContent
            event={event}
            offer={offer}
            addons={availableAddons ?? []}
            quantity={quantity}
            selectedAddons={selectedAddonIds}
            showNominativeFields={offer.settings.isNominative}
            onContinue={handleSubmit(onSubmit)}
            handleClose={handleCloseModal}
            title={t('offers:fillInfoOfflineSalesTitle')}
            mainActionText={t('offers:confirmSaleButtonText')}
            isSubmitLoading={isSubmitting}
          />
        ) : (
          <SummaryFormMultiModalContent
            event={event}
            offer={offer}
            addons={availableAddons ?? []}
            quantity={quantity}
            selectedAddons={selectedAddonIds}
            onContinue={handleSubmit(onSubmit)}
            handleClose={handleCloseModal}
            title={t('offers:fillInfoOfflineSalesTitle')}
            mainActionText={t('offers:confirmSaleButtonText')}
            isSubmitLoading={isSubmitting}
          />
        ))
      );
    case STEP_SUCCESS:
      return (
        offer &&
        addGuestData && (
          <>
            <SummarySuccessModalContent
              offer={offer}
              addons={availableAddons ?? []}
              quantity={quantity}
              bookingLegacyId={addGuestData?.legacyId}
              onClickMainAction={() => setStep(entryStep)}
              onClickSecondaryAction={() => checkAndDownload(addGuestData.token, getBookingLinkUrl(addGuestData.token))}
              secondaryActionIcon={IconDownload}
              secondaryActionText={t('offers:downloadPdf')}
              onClickClose={closeModal}
              title={t('offers:processOfflineSalesSuccessTitle')}
              headerDescription={
                email ? (
                  <span className='text-secondary text-body-s'>
                    <Trans
                      i18nKey='offers:processEmailBookingSuccessDescription'
                      components={{ b: <b /> }}
                      values={{ email, bookingLegacyId: addGuestData.legacyId }}
                    />
                  </span>
                ) : undefined
              }
              mainActionText={t('offers:buttonSaleMore')}
            />
            <FileDownloadModal {...downloadModalProps} />
          </>
        )
      );
    default:
      return null;
  }
};
